import React, { useState } from 'react';
import {
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  Divider,
  Box,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { formatCurrency } from '../../../utils/formatters';
import { approveIRSForm, rejectIRSForm } from '../../../endpoints/irsForms';
import { IRSFormDetail, IRSQuestion } from '../../../types/irsForms';

interface IRSFormSummaryProps {
  formDetail: IRSFormDetail;
  questions: IRSQuestion[];
  editable: boolean;
  onApprove?: () => void;
  onReject?: () => void;
}

const IRSFormSummary: React.FC<IRSFormSummaryProps> = ({
  formDetail,
  questions,
  editable,
  onApprove,
  onReject,
}) => {
  const [signature, setSignature] = useState('');
  const [understandChecked, setUnderstandChecked] = useState(false);
  const [rejectionNote, setRejectionNote] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);

  const currentUserId = localStorage.getItem('user_id');

  // Check if current user is an approver for the current status
  const isApprover = formDetail.approvers.some(
    (approver) =>
      approver.status_id === formDetail.status_id &&
      approver.user_id === currentUserId
  );

  // Calculate totals from decimal answers
  const calculateTotals = () => {
    let total = 0;
    questions.forEach((q) => {
      if (q.answer_type === 'decimal' && q.decimal_answer) {
        total += q.decimal_answer;
      } else if (q.answer_type === 'other_category' && q.other_line_items) {
        q.other_line_items.forEach((item) => {
          if (item.amount) {
            total += item.amount;
          }
        });
      }
    });
    return total;
  };

  const handleApprove = async () => {
    if (!signature.trim() || !understandChecked) {
      return;
    }

    setIsApproving(true);
    try {
      await approveIRSForm(formDetail.id, {
        signature: signature.trim(),
        understand_checked: understandChecked,
        totals: {}, // Backend may validate totals
      });
      if (onApprove) {
        onApprove();
      }
      setSignature('');
      setUnderstandChecked(false);
    } catch (error) {
      console.error('Failed to approve form:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionNote.trim()) {
      return;
    }

    setIsRejecting(true);
    try {
      await rejectIRSForm(formDetail.id, {
        note: rejectionNote.trim(),
      });
      if (onReject) {
        onReject();
      }
      setRejectionNote('');
      setShowRejectionForm(false);
    } catch (error) {
      console.error('Failed to reject form:', error);
    } finally {
      setIsRejecting(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Form Summary
          </Typography>
          <Divider sx={{ my: 2 }} />

          {/* Previous Ending Balance */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" fontWeight="bold">
                Previous Ending Balance:
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1">
                {formDetail.previous_ending_balance !== null &&
                formDetail.previous_ending_balance !== undefined
                  ? formatCurrency(formDetail.previous_ending_balance)
                  : 'N/A'}
              </Typography>
            </Grid>
          </Grid>

          {/* Calculated Totals */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" fontWeight="bold">
                Form Totals:
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1">{formatCurrency(totals)}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Approval/Rejection Section */}
          {isApprover && editable && (
            <Grid container spacing={2}>
              {!showRejectionForm ? (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Approve Form
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Signature"
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      fullWidth
                      required
                      disabled={isApproving}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={understandChecked}
                          onChange={(e) =>
                            setUnderstandChecked(e.target.checked)
                          }
                          disabled={isApproving}
                        />
                      }
                      label="I understand and confirm the information is accurate"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={handleApprove}
                        disabled={!signature.trim() || !understandChecked}
                        loading={isApproving}
                      >
                        Approve Form
                      </LoadingButton>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setShowRejectionForm(true)}
                        disabled={isApproving}
                      >
                        Reject Form
                      </Button>
                    </Box>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Reject Form
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Rejection Note"
                      value={rejectionNote}
                      onChange={(e) => setRejectionNote(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                      required
                      disabled={isRejecting}
                      helperText="Please provide a reason for rejection"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <LoadingButton
                        variant="contained"
                        color="error"
                        onClick={handleReject}
                        disabled={!rejectionNote.trim()}
                        loading={isRejecting}
                      >
                        Submit Rejection
                      </LoadingButton>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowRejectionForm(false);
                          setRejectionNote('');
                        }}
                        disabled={isRejecting}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {!isApprover && (
            <Typography variant="body2" color="text.secondary">
              You are not an approver for this form at the current status.
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default IRSFormSummary;
